import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft,
  Store,
  DollarSign,
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  ChevronRight
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VendorDetailPage = () => {
  const { vendorName } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchVendorDetail = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/vendors-analytics/detail/${encodeURIComponent(vendorName)}`,
        { withCredentials: true }
      );
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load vendor details');
      navigate('/vendors');
    } finally {
      setLoading(false);
    }
  }, [vendorName, navigate]);

  useEffect(() => {
    fetchVendorDetail();
  }, [fetchVendorDetail]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  const formatMonth = (monthStr) => {
    try {
      const [year, month] = monthStr.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    } catch {
      return monthStr;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Draft': return 'bg-zinc-100 text-zinc-700';
      case 'Ordered': return 'bg-blue-500 text-white';
      case 'Confirmed': return 'bg-purple-500 text-white';
      case 'Shipped': return 'bg-orange-500 text-zinc-900';
      case 'Delivered': return 'bg-green-500 text-white';
      case 'Issue': return 'bg-red-500 text-white';
      default: return 'bg-zinc-100';
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-zinc-200 p-3 ">
          <p className="text-sm font-bold">{formatMonth(label)}</p>
          <p className="text-lg font-heading font-bold uppercase tracking-wide">{formatCurrency(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!data) return null;

  const { summary, order_history, spend_trend, products_ordered } = data;
  const onTimePlusLate = summary.on_time_count + summary.late_count;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="vendor-detail-page">
      {/* Back Button */}
      <button
        onClick={() => navigate('/vendors')}
        data-testid="back-to-vendors-btn"
        className="inline-flex items-center gap-2 text-sm font-bold mb-4 hover:text-primary"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Vendors
      </button>

      {/* Vendor Header */}
      <div className="bg-white border border-zinc-200 p-4 sm:p-6  mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-primary border border-zinc-200 flex items-center justify-center flex-shrink-0">
            <Store className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold uppercase tracking-wide" data-testid="vendor-name">{data.vendor_name}</h1>
            <p className="text-zinc-500 text-sm">
              {summary.order_count} order{summary.order_count !== 1 ? 's' : ''} &middot; {formatCurrency(summary.total_spent)} total
            </p>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-zinc-200 p-4 " data-testid="stat-total-spent">
          <DollarSign className="w-7 h-7 text-primary mb-2" />
          <p className="text-2xl font-heading font-bold uppercase tracking-wide">{formatCurrency(summary.total_spent)}</p>
          <p className="text-xs font-bold uppercase text-zinc-500">Total Spent</p>
        </div>
        <div className="bg-white border border-zinc-200 p-4 " data-testid="stat-order-count">
          <Package className="w-7 h-7 text-blue-500 mb-2" />
          <p className="text-2xl font-heading font-bold uppercase tracking-wide">{summary.order_count}</p>
          <p className="text-xs font-bold uppercase text-zinc-500">Total Orders</p>
        </div>
        <div className="bg-white border border-zinc-200 p-4 " data-testid="stat-avg-order">
          <TrendingUp className="w-7 h-7 text-zinc-500 mb-2" />
          <p className="text-2xl font-heading font-bold uppercase tracking-wide">{formatCurrency(summary.avg_order_size)}</p>
          <p className="text-xs font-bold uppercase text-zinc-500">Avg Order Size</p>
        </div>
        <div className="bg-white border border-zinc-200 p-4 " data-testid="stat-on-time">
          <Clock className="w-7 h-7 text-success mb-2" />
          <p className="text-2xl font-heading font-bold uppercase tracking-wide">
            {summary.on_time_pct !== null ? `${summary.on_time_pct}%` : 'N/A'}
          </p>
          <p className="text-xs font-bold uppercase text-zinc-500">On-Time Delivery</p>
        </div>
      </div>

      {/* Delivery Performance */}
      {onTimePlusLate > 0 && (
        <div className="bg-white border border-zinc-200 p-4 sm:p-6  mb-6" data-testid="delivery-performance">
          <h2 className="text-lg font-heading font-bold uppercase tracking-wide mb-4">Delivery Performance</h2>
          <div className="flex items-center gap-4 mb-3">
            <div className="flex-1">
              <div className="h-8 bg-zinc-100 border border-zinc-200 overflow-hidden flex">
                <div
                  className="h-full bg-green-500 transition-all"
                  style={{ width: `${(summary.on_time_count / onTimePlusLate) * 100}%` }}
                />
                <div
                  className="h-full bg-red-500 transition-all"
                  style={{ width: `${(summary.late_count / onTimePlusLate) * 100}%` }}
                />
              </div>
            </div>
          </div>
          <div className="flex justify-between text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-success" />
              <span className="font-bold text-success">{summary.on_time_count} on time</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-destructive" />
              <span className="font-bold text-destructive">{summary.late_count} late</span>
            </div>
          </div>
          {summary.avg_delivery_days !== null && (
            <p className="text-sm text-zinc-500 mt-2">
              Average delivery: {summary.avg_delivery_days <= 0 
                ? `${Math.abs(summary.avg_delivery_days)} days early`
                : `${summary.avg_delivery_days} days late`
              }
            </p>
          )}
        </div>
      )}

      {/* Spend Trend Chart */}
      <div className="bg-white border border-zinc-200 p-4 sm:p-6  mb-6" data-testid="spend-trend-chart">
        <h2 className="text-lg font-heading font-bold uppercase tracking-wide mb-4">Spend Trend (Last 6 Months)</h2>
        {spend_trend.every(s => s.amount === 0) ? (
          <p className="text-zinc-500 text-center py-8">No spending data in the last 6 months</p>
        ) : (
          <div className="h-64 sm:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={spend_trend} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis
                  dataKey="month"
                  tickFormatter={formatMonth}
                  tick={{ fontSize: 14, fontWeight: 700, fill: '#000' }}
                  axisLine={{ stroke: '#000', strokeWidth: 2 }}
                  tickLine={{ stroke: '#000' }}
                />
                <YAxis
                  tickFormatter={(val) => `$${(val / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 12, fontWeight: 700, fill: '#808080' }}
                  axisLine={{ stroke: '#000', strokeWidth: 2 }}
                  tickLine={{ stroke: '#000' }}
                  width={60}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="amount" radius={[4, 4, 0, 0]} maxBarSize={60}>
                  {spend_trend.map((entry, index) => (
                    <Cell key={`cell-${entry.month}-${index}`} fill={entry.amount > 0 ? '#FFC40C' : '#e5e5e5'} stroke="#000" strokeWidth={2} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Products Ordered */}
      <div className="bg-white border border-zinc-200 p-4 sm:p-6  mb-6" data-testid="products-ordered">
        <h2 className="text-lg font-heading font-bold uppercase tracking-wide mb-4">Products Ordered</h2>
        {products_ordered.length === 0 ? (
          <p className="text-zinc-500">No product data available</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="pb-3 text-xs font-heading font-bold uppercase tracking-wide uppercase text-zinc-500 pr-4">Product</th>
                  <th className="pb-3 text-xs font-heading font-bold uppercase tracking-wide uppercase text-zinc-500 text-right pr-4">Qty</th>
                  <th className="pb-3 text-xs font-heading font-bold uppercase tracking-wide uppercase text-zinc-500 text-right pr-4">Avg Price</th>
                  <th className="pb-3 text-xs font-heading font-bold uppercase tracking-wide uppercase text-zinc-500 text-right">Latest Price</th>
                </tr>
              </thead>
              <tbody>
                {products_ordered.map((product, idx) => (
                  <tr key={`${product.product_description}-${product.unit}-${idx}`} className="border-b border-gray-200 last:border-b-0" data-testid={`product-row-${idx}`}>
                    <td className="py-3 pr-4">
                      <p className="font-bold text-sm">{product.product_description}</p>
                      <p className="text-xs text-zinc-500">{product.unit}</p>
                    </td>
                    <td className="py-3 pr-4 text-right font-bold">{product.total_quantity}</td>
                    <td className="py-3 pr-4 text-right text-zinc-500">{formatCurrency(product.avg_unit_price)}</td>
                    <td className="py-3 text-right font-bold">{formatCurrency(product.most_recent_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Order History */}
      <div className="bg-white border border-zinc-200 p-4 sm:p-6 " data-testid="order-history">
        <h2 className="text-lg font-heading font-bold uppercase tracking-wide mb-4">Order History</h2>
        {order_history.length === 0 ? (
          <p className="text-zinc-500">No orders found</p>
        ) : (
          <div className="space-y-3">
            {order_history.map((order) => (
              <button
                key={order.order_id}
                onClick={() => navigate(`/projects/${order.project_id}`)}
                data-testid={`order-history-${order.order_id}`}
                className="w-full flex items-center gap-4 p-3 sm:p-4 border border-zinc-200 bg-zinc-50 hover:bg-zinc-50 text-left transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`px-2 py-0.5 text-xs font-bold uppercase border border-black ${getStatusClass(order.status)}`}>
                      {order.status}
                    </span>
                    {order.po_number && (
                      <span className="text-xs font-mono bg-white px-2 py-0.5 border border-black">
                        {order.po_number}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium truncate">{order.project_name}</p>
                  <p className="text-xs text-zinc-500">
                    {formatDate(order.order_date)} &middot; {order.line_item_count} item{order.line_item_count !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-heading font-bold uppercase tracking-wide">{formatCurrency(order.total)}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-zinc-500 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorDetailPage;
